import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { API, useAuth } from "@/App";
import { Sparkles } from "lucide-react";
import { resolveAssetUrl } from "@/lib/assetUrl";

/**
 * Horizontal "Because you saved X" rail.
 * Renders above the main feed for signed-in users with ≥3 saves. Hidden
 * for guests and light users to keep the home page uncluttered.
 */
export default function PersonalisedRail() {
  const { user } = useAuth();
  const [data, setData] = useState({ pins: [], seed_category_label: null, seed_title: null });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!user) return;
    setLoading(true);
    axios
      .get(`${API}/blinterest/personalised?limit=12`, { withCredentials: true })
      .then((r) => setData(r.data || { pins: [] }))
      .catch(() => setData({ pins: [] }))
      .finally(() => setLoading(false));
  }, [user]);

  if (!user) return null;
  if (!loading && (!data.pins || data.pins.length === 0)) return null;

  const label = data.seed_title
    ? `Because you kept “${data.seed_title.slice(0, 40)}${data.seed_title.length > 40 ? "…" : ""}”`
    : `More in ${data.seed_category_label || "your interests"}`;

  return (
    <section className="mb-8 min-w-0" data-testid="personalised-rail">
      <div className="flex items-center gap-2 mb-3">
        <Sparkles className="w-4 h-4 text-tbc-gold" />
        <h2 className="font-heading text-white text-lg">{label}</h2>
      </div>
      {/* overflow contained via `overflow-hidden` on the outer + `overflow-x-auto`
          on the inner track so the rail never bleeds horizontal scroll to the
          whole page on mobile. */}
      <div className="overflow-hidden">
        <div className="flex gap-3 overflow-x-auto pb-2 snap-x scrollbar-hidden">
          {loading
            ? [1, 2, 3, 4, 5, 6].map((i) => (
                <div
                  key={i}
                  className="w-40 flex-none aspect-[3/4] bg-white/5 rounded-xl animate-pulse"
                />
              ))
            : data.pins.map((p) => (
                <Link
                  to={`/blinterest/pin/${p.pin_id}`}
                  key={p.pin_id}
                  className="w-40 flex-none snap-start rounded-xl overflow-hidden bg-white/5 border border-white/5 hover:border-tbc-gold/40 transition-colors group"
                  data-testid={`personalised-pin-${p.pin_id}`}
                >
                  <div className="aspect-[3/4] overflow-hidden">
                    <img
                      src={resolveAssetUrl(p.image_url)}
                      alt={p.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform"
                      loading="lazy"
                    />
                  </div>
                  <p className="text-white/80 text-xs font-body p-2 line-clamp-2">{p.title}</p>
                </Link>
              ))}
        </div>
      </div>
    </section>
  );
}
